import { shallowRef, computed, onMounted, onUnmounted } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { eachDayOfInterval, format, parseISO, subDays } from 'date-fns';
import { tz } from '@date-fns/tz';
import { queryKeys } from '../../../../api/queryKeys.js';
import { compareByAvailable, isLowBudget } from '../utils/budget.js';
import {
	fetchStudents,
	fetchEmployees,
	fetchAccountAnalytics,
} from '../../../../api/queries/accountQueries.js';

const berlinTz = tz('Europe/Berlin');

const ONE_MINUTE_MS = 60 * 1000;

/**
 * Data layer for the users admin dashboard.
 *
 * Owns:
 * - students / employees / analytics TanStack queries
 * - analytics range state (days) and Berlin calendar from/until labels
 * - pending vs fetching flags and query errors
 * - refresh action and date-anchor (Berlin YMD)
 */
export function useUsersDashboardData() {
	const queryClient = useQueryClient();

	/** Selected analytics window in days (7 | 14 | 30). */
	const analyticsRangeDays = shallowRef(30);
	/** Berlin calendar day used as the inclusive until-date of the window. */
	const dateAnchor = shallowRef(format(new Date(), 'yyyy-MM-dd', { in: berlinTz }));

	const bumpDateAnchor = () => {
		dateAnchor.value = format(new Date(), 'yyyy-MM-dd', { in: berlinTz });
	};

	let dateAnchorIntervalId = null;

	onMounted(() => {
		dateAnchorIntervalId = setInterval(bumpDateAnchor, ONE_MINUTE_MS);
	});

	onUnmounted(() => {
		if (dateAnchorIntervalId !== null) {
			clearInterval(dateAnchorIntervalId);
			dateAnchorIntervalId = null;
		}
	});

	const analyticsDates = computed(() => {
		const until = parseISO(dateAnchor.value);
		const start = subDays(until, analyticsRangeDays.value - 1);

		return eachDayOfInterval({ start, end: until }).map((day) => format(day, 'yyyy-MM-dd'));
	});

	const analyticsFrom = computed(() => analyticsDates.value[0]);
	const analyticsUntil = computed(() => analyticsDates.value[analyticsDates.value.length - 1]);

	const {
		data: studentsData,
		isPending: isPendingStudents,
		isFetching: isFetchingStudents,
		error: studentsError,
	} = useQuery({
		queryKey: queryKeys.accountStudents(),
		queryFn: fetchStudents,
		staleTime: 5 * 60 * 1000,
	});

	const {
		data: employeesData,
		isPending: isPendingEmployees,
		isFetching: isFetchingEmployees,
		error: employeesError,
	} = useQuery({
		queryKey: queryKeys.accountEmployees(),
		queryFn: fetchEmployees,
		staleTime: 5 * 60 * 1000,
	});

	const employeesList = computed(() => {
		if (Array.isArray(employeesData.value)) {
			return employeesData.value;
		}
		return employeesData.value?.employees || [];
	});

	const {
		data: accountAnalytics,
		isPending: isPendingAnalytics,
		isFetching: isFetchingAnalytics,
		error: analyticsError,
	} = useQuery({
		queryKey: computed(() => queryKeys.accountAnalytics({
			from: analyticsFrom.value,
			until: analyticsUntil.value,
		})),
		queryFn: () => fetchAccountAnalytics({
			from: analyticsFrom.value,
			until: analyticsUntil.value,
		}),
		staleTime: 5 * 60 * 1000,
		retry: 0,
	});

	const isBaseLoading = computed(
		() => isPendingStudents.value || isPendingEmployees.value,
	);

	const isBaseFetching = computed(
		() => isFetchingStudents.value || isFetchingEmployees.value,
	);

	const baseError = computed(() => studentsError.value || employeesError.value || null);

	const lowBudgetStudents = computed(() => {
		return (studentsData.value || []).filter(isLowBudget).sort(compareByAvailable);
	});

	const isRefreshDisabled = computed(() => isBaseFetching.value || isFetchingAnalytics.value);

	// Background refetch only; the initial load shows skeletons instead of a spinning icon
	const isRefreshing = computed(
		() => isRefreshDisabled.value && !isBaseLoading.value && !isPendingAnalytics.value,
	);

	const refreshAllData = () => {
		bumpDateAnchor();
		queryClient.invalidateQueries({ queryKey: queryKeys.accountStudents() });
		queryClient.invalidateQueries({ queryKey: queryKeys.accountEmployees() });
		queryClient.invalidateQueries({ queryKey: queryKeys.accountAnalytics() });
	};

	return {
		analyticsRangeDays,
		studentsData,
		employeesList,
		lowBudgetStudents,
		accountAnalytics,
		isBaseLoading,
		isBaseFetching,
		isBookingsLoading: isPendingAnalytics,
		isBookingsFetching: isFetchingAnalytics,
		baseError,
		analyticsError,
		isRefreshDisabled,
		isRefreshing,
		refreshAllData,
	};
}
